<template>
  <span
    ref="circleRef"
    class="annotation-circle"
    :class="{
      'annotation-circle--linked': linked,
      'annotation-circle--hovered': hovered,
      'annotation-circle--small': size === 'small',
      'annotation-circle--large': size === 'large',
      'annotation-circle--extra-large': size === 'extra-large',
    }"
  >
    <span class="annotation-circle__text" :style="textStyle"><slot /></span>
  </span>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, useSlots, nextTick } from 'vue';

withDefaults(
  defineProps<{
    linked?: boolean;
    hovered?: boolean;
    size?: 'small' | 'medium' | 'large' | 'extra-large';
  }>(),
  {
    linked: false,
    hovered: false,
    size: 'medium',
  }
);

const circleRef = ref<HTMLElement | null>(null);
const scale = ref(1);

function measure() {
  const el = circleRef.value;
  if (!el) return;
  const inner = el.querySelector('.annotation-circle__text') as HTMLElement | null;
  if (!inner) return;
  inner.style.transform = 'none';
  const availableWidth = el.clientWidth - 4;
  const textWidth = inner.scrollWidth;
  scale.value = textWidth > availableWidth ? availableWidth / textWidth : 1;
}

const textStyle = computed(() => (scale.value < 1 ? { transform: `scale(${scale.value})` } : undefined));

const slots = useSlots();
onMounted(measure);
watch(
  () => slots.default?.(),
  () => nextTick(measure)
);
</script>

<style scoped lang="scss">
@use '@styles/variables' as *;

.annotation-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  min-width: 2.2rem;
  border: 2px solid $c-dark;
  border-radius: 50%;
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: $c-dark;
  background: white;
  overflow: hidden;
  transition:
    background-color 0.1s ease,
    color 0.1s ease;

  &__text {
    white-space: nowrap;
    transform-origin: center;
    line-height: 1;
  }

  &--linked {
    background-color: rgba($c-blue, 0.1);
    border-color: $c-blue;
    color: $c-blue;
  }

  &--hovered {
    background-color: $c-dark;
    color: white;
  }

  &--small {
    width: 2rem;
    height: 2rem;
    min-width: 2rem;
    font-size: 0.9rem;
  }

  &--large {
    width: 2.6rem;
    height: 2.6rem;
    min-width: 2.6rem;
    font-size: 1.2rem;
  }

  &--extra-large {
    width: 3.6rem;
    height: 3.6rem;
    min-width: 3.6rem;
    font-size: 1.2rem;
  }
}
</style>
